import React from 'react';
import { Clock } from 'lucide-react';
import { ProductSkeleton } from './skeletons';
import LazyImage from './LazyImage';
import FeaturedProductsCarousel from '../FeaturedProductsCarousel';
import ProductGridView from '../ProductGridView';

// HomeScreen do cardápio público (lista/grid de produtos + banners de status).
// Extraído VERBATIM de PublicMenu.js (Fase 2 refactor). Mantido como FUNÇÃO
// (chamado inline como {HomeScreen({...})}, não como componente JSX) para preservar
// exatamente o comportamento de render inline do componente pai.
const HomeScreen = ({ loading, products, layout, groupedProducts, restaurant, refreshCache, homeScrollTopRef, featuredProducts, navigateToScreen, filteredProducts }) => {
    // 🚀 SKELETON LOADER: Mostra estrutura enquanto carrega
    if (loading && products.length === 0) {
      return (
        <div className="pt-28 pb-20 px-4">
          {/* Header skeleton */}
          <div className="mb-6 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-3/4" />
          </div>
          
          {/* Featured section skeleton */}
          <div className="mb-8">
            <div className="h-5 bg-gray-200 rounded w-32 mb-4" />
            <div className="flex gap-3 overflow-x-hidden">
              {[1, 2, 3].map(i => (
                <div key={i} className="w-36 flex-shrink-0 animate-pulse">
                  <div className="aspect-square bg-gray-200 rounded-xl mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-1" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          </div>
          
          {/* Categories tabs skeleton */}
          <div className="flex gap-2 mb-6 overflow-x-hidden">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-8 w-24 bg-gray-200 rounded-full animate-pulse" />
            ))}
          </div>
          
          {/* Products skeleton */}
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <ProductSkeleton key={i} layout={layout} />
            ))}
          </div>
        </div>
      );
    }

    // Se não tem produtos mas a loja está expirada/suspensa, mostrar mensagem específica
    if (groupedProducts.length === 0) {
      const isExpiredOrSuspended = restaurant?.status?.is_expired || restaurant?.status?.is_suspended;
      
      if (isExpiredOrSuspended) {
        return (
          <div className="pt-28 pb-20 px-4 flex items-center justify-center min-h-screen">
            <div className="text-center max-w-sm mx-auto">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                {restaurant?.status?.is_suspended ? '🔒 Loja Suspensa' : '⏰ Loja Temporariamente Fechada'}
              </h2>
              <p className="text-gray-600 mb-4">
                Esta loja não está aceitando pedidos no momento.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Entre em contato com o estabelecimento para mais informações.
              </p>
              <button
                onClick={refreshCache}
                className="text-sm text-blue-500 hover:text-blue-700 underline"
              >
                Tentar atualizar cardápio
              </button>
            </div>
          </div>
        );
      }
      
      // Loja ativa mas sem produtos - oferecer botão de refresh
      return (
        <div className="pt-28 pb-20 px-4 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-gray-600 text-lg mb-4">Cardápio em atualização</p>
            <p className="text-gray-500 mb-6">Em breve novos produtos!</p>
            <button
              onClick={refreshCache}
              className="bg-primary text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-primary-dark transition-colors"
            >
              🔄 Atualizar cardápio
            </button>
          </div>
        </div>
      );
    }

    return (
      <div id="home-scroll-container" onScroll={(e) => { homeScrollTopRef.current = e.currentTarget.scrollTop; }} className={`fixed inset-0 pb-20 px-4 overflow-y-auto ${
        restaurant?.manually_closed ? 'pt-52' : 'pt-40'
      }`} style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* Banner para Loja Expirada ou Suspensa */}
        {(restaurant?.status?.is_expired || restaurant?.status?.is_suspended) && (
          <div className="mb-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 shadow-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-base font-bold text-white">
                  {restaurant?.status?.is_suspended ? '🔒 Loja Suspensa' : '⏰ Loja Temporariamente Fechada'}
                </h3>
                <p className="text-sm text-white/90 mt-1">
                  Esta loja não está aceitando pedidos no momento. Você pode visualizar o cardápio normalmente.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Banner para Horário Fechado (apenas se NÃO for expirada/suspensa) */}
        {!restaurant?.status?.is_expired && !restaurant?.status?.is_suspended && restaurant?.status?.is_auto && !restaurant.status.is_open && (
          <div className={`mb-6 rounded-lg p-4 border-2 ${
            restaurant?.scheduling?.enabled && restaurant?.scheduling?.available
              ? 'bg-amber-50 border-amber-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  restaurant?.scheduling?.enabled && restaurant?.scheduling?.available
                    ? 'bg-amber-500'
                    : 'bg-red-500'
                }`}>
                  <Clock className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="ml-3 flex-1">
                <h3 className={`text-sm font-semibold ${
                  restaurant?.scheduling?.enabled && restaurant?.scheduling?.available
                    ? 'text-amber-800'
                    : 'text-red-800'
                }`}>
                  Estamos fechados no momento
                </h3>
                <p className={`text-sm mt-0.5 ${
                  restaurant?.scheduling?.enabled && restaurant?.scheduling?.available
                    ? 'text-amber-700'
                    : 'text-red-700'
                }`}>
                  {restaurant.status.message}
                </p>
              </div>
              {/* Badge de Agendamento Disponível */}
              {restaurant?.scheduling?.enabled && restaurant?.scheduling?.available && (
                <div className="flex-shrink-0 ml-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Agendar
                  </span>
                </div>
              )}
            </div>
            {/* Mensagem extra quando agendamento está disponível */}
            {restaurant?.scheduling?.enabled && restaurant?.scheduling?.available && (
              <p className="text-xs text-amber-600 mt-2 ml-13 pl-[52px]">
                💡 Adicione itens ao carrinho e agende seu pedido para quando abrirmos!
              </p>
            )}
          </div>
        )}
        
        {/* Conditional Rendering: Grid or List */}
        {/* Featured Products Carousel - Exibir se houver produtos em destaque */}
        {featuredProducts && featuredProducts.length > 0 && (
          <FeaturedProductsCarousel
            products={featuredProducts}
            onProductClick={(product) => {
              navigateToScreen('product', product);
            }}
          />
        )}
        
        {layout === 'grid' ? (
          <ProductGridView 
            categories={filteredProducts}
            onProductClick={(product) => {
              navigateToScreen('product', product);
            }}
          />
        ) : (
          /* List View (Original) */
          filteredProducts.map(category => (
            <div key={category.id} id={`category-${category.id}`} className="mb-8">
              <div className="flex items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{category.name}</h2>
              </div>
              <div className="space-y-4">
                {category.products.map(product => (
                  <div 
                    key={product.id} 
                    className="bg-white rounded-lg shadow-sm p-4 flex items-center space-x-4 transition-all duration-300 hover:shadow-md cursor-pointer"
                    onClick={() => {
                      navigateToScreen('product', product);
                    }}
                  >
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{product.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <p className="text-lg font-bold text-gray-900">
                          {product.price > 0 ? `R$ ${product.price.toFixed(2)}` : 'Ver opções'}
                        </p>
                        {product.preparation_time && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock size={12} />
                            {product.preparation_time}min
                          </span>
                        )}
                      </div>
                    </div>
                    <LazyImage 
                      src={product.image_url || "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg"} 
                      alt={product.name}
                      className="w-24 h-24 rounded-lg flex-shrink-0"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
};

export default HomeScreen;
